<script lang="ts">
  import { snapToStep, tickValue } from './sliderTicks';

  interface Props {
    label: string;
    value: number;
    min: number;
    max: number;
    step?: number;
    onChange?: (value: number) => void;
    ticks?: string[];
    /** Tick labels over the track instead of under it. */
    ticksAbove?: boolean;
    hint?: string;
    /** Stays on the number's line so narrow viewports don't orphan the unit. */
    valueSuffix?: string;
    /** Shown and announced instead of the number, for an INDEX over named stops. */
    valueText?: string;
    ariaLabel?: string;
    id?: string;
    disabled?: boolean;
    class?: string;
    /**
     * Track and value on ONE line, with no visible label — for a slider whose
     * name is already printed beside it (the review options' label column). The
     * accessible name still comes from `ariaLabel ?? label`.
     */
    compact?: boolean;
    /**
     * Greys the fill and thumb: the value is not one of the named stops, so the
     * track must not claim a position it is not in.
     */
    muted?: boolean;
    /** Makes the label's value an editable number with this accessible name. */
    numberLabel?: string;
    /** Hint shown while the typed value is outside min..max, given where it will settle. */
    outOfRangeHint?: (settled: number) => string;
  }

  let {
    label,
    value = $bindable(),
    min,
    max,
    step = 1,
    onChange,
    ticks,
    ticksAbove = false,
    hint,
    valueSuffix = '',
    valueText,
    ariaLabel,
    id,
    disabled = false,
    class: klass = '',
    compact = false,
    muted = false,
    numberLabel,
    outOfRangeHint,
  }: Props = $props();

  // Minted once, not reactive, so the `<label for>` link stays put.
  const fallbackId = `vd-slider-${Math.random().toString(36).slice(2, 9)}`;
  const inputId = $derived(id ?? fallbackId);
  const hintId = $derived(`${inputId}-hint`);

  function onchange(): void {
    onChange?.(value);
  }

  // What the user has typed but not yet committed; `null` means the field simply
  // mirrors the track. Kept raw so an in-progress "1" on the way to "150" is
  // not clamped up to the minimum under the caret.
  let typed = $state<string | null>(null);
  const fieldValue = $derived(typed ?? String(value));
  const typedNumber = $derived(typed === null || typed.trim() === '' ? NaN : Number(typed));
  const outOfRange = $derived(
    Number.isFinite(typedNumber) && (typedNumber < min || typedNumber > max),
  );
  const activeHint = $derived(
    outOfRange && outOfRangeHint ? outOfRangeHint(snapToStep(typedNumber, min, max, step)) : hint,
  );

  function commit(next: number): void {
    const snapped = snapToStep(next, min, max, step);
    typed = null;
    if (snapped === value) return;
    value = snapped;
    onchange();
  }

  function onNumberInput(event: Event): void {
    typed = (event.currentTarget as HTMLInputElement).value;
    if (!outOfRange && Number.isFinite(typedNumber)) {
      const snapped = snapToStep(typedNumber, min, max, step);
      if (snapped !== value) {
        value = snapped;
        onchange();
      }
    }
  }

  const fillPercent = $derived(max > min ? ((value - min) / (max - min)) * 100 : 0);

  const tickValues = $derived(
    (ticks ?? []).map((_, i) => tickValue(i, ticks?.length ?? 0, min, max, step)),
  );

  function selectTick(i: number): void {
    if (disabled) return;
    commit(tickValues[i]);
  }
</script>

{#snippet range()}
  <input
    id={inputId}
    type="range"
    {min}
    {max}
    {step}
    {disabled}
    style="--vd-slider-fill: {fillPercent}%"
    bind:value
    oninput={() => {
      typed = null;
    }}
    {onchange}
    aria-label={ariaLabel ?? label}
    aria-valuetext={valueText}
  />
{/snippet}

{#snippet tickRow()}
  {#if ticks && ticks.length}
    <!-- Pointer shortcut; the input owns the keyboard path — never focusable. -->
    <div class="vd-slider__ticks" aria-hidden="true">
      {#each ticks as tick, i (i)}<button
          type="button"
          tabindex="-1"
          {disabled}
          class:is-current={tickValues[i] === value}
          onclick={() => selectTick(i)}>{tick}</button
        >{/each}
    </div>
  {/if}
{/snippet}

<div
  class="vd-slider {klass}"
  class:vd-slider--compact={compact}
  class:vd-slider--ticked={compact && !!ticks?.length}
  class:vd-slider--ticks-above={ticksAbove}
  class:vd-slider--muted={muted}
>
  {#if compact}
    {@render range()}
    <strong class="vd-slider__value"
      >{valueText ?? `${value}${valueSuffix ? ` ${valueSuffix}` : ''}`}</strong
    >
    {@render tickRow()}
  {:else}
    {#if numberLabel}
      <div class="vd-slider__label">
        <label for={inputId}>{label}:</label>
        <!-- A label of its own, so its touch-sized hit area focuses the input. -->
        <label class="vd-slider__field" class:is-out={outOfRange}>
          <input
            type="number"
            inputmode="numeric"
            {min}
            {max}
            {step}
            {disabled}
            value={fieldValue}
            style="--vd-slider-digits: {String(max).length}"
            aria-label={numberLabel}
            aria-invalid={outOfRange ? true : undefined}
            aria-describedby={activeHint ? hintId : undefined}
            oninput={onNumberInput}
            onchange={() => commit(typed === null || typed.trim() === '' ? value : Number(typed))}
            onkeydown={(e) => {
              if (e.key === 'Enter') e.currentTarget.blur();
              const dir = e.key === 'ArrowUp' ? 1 : e.key === 'ArrowDown' ? -1 : 0;
              if (dir === 0) return;
              e.preventDefault();
              commit(value + dir * step);
            }}
          />{#if valueSuffix}<span>{valueSuffix}</span>{/if}
        </label>
      </div>
    {:else}
      <label class="vd-slider__label" for={inputId}>
        {label}:
        <strong class="vd-slider__value"
          >{valueText ?? `${value}${valueSuffix ? ` ${valueSuffix}` : ''}`}</strong
        >
      </label>
    {/if}
    {#if ticksAbove}{@render tickRow()}{/if}
    {@render range()}
    {#if !ticksAbove}{@render tickRow()}{/if}
  {/if}
  {#if activeHint}
    <p id={hintId} class="vd-slider__hint" class:is-out={outOfRange}>{activeHint}</p>
  {/if}
</div>

<style lang="scss">
  /* Unbordered: every Slider sits inside a bordered Surface already. */
  .vd-slider {
    display: flex;
    flex-direction: column;
    gap: 0.4rem;
  }

  .vd-slider--compact {
    flex-direction: row;
    align-items: center;
    gap: 0.7rem;
  }

  .vd-slider--compact input[type='range'] {
    flex: 1 1 auto;
    min-width: 6rem;
    max-width: 13rem;
  }

  .vd-slider--compact .vd-slider__value {
    color: var(--ink);
  }

  /* Compact with ticks: the track keeps the value beside it; the ticks span the track alone. */
  .vd-slider--ticked {
    display: grid;
    grid-template-columns: minmax(6rem, 1fr) auto;
    align-items: center;
    column-gap: 0.7rem;
    row-gap: 0;
  }

  .vd-slider.vd-slider--ticked input[type='range'] {
    grid-area: 2 / 1;
    max-width: none;
    height: 44px;
  }

  .vd-slider--ticked .vd-slider__value {
    grid-area: 2 / 2;
  }

  .vd-slider--ticked .vd-slider__ticks {
    grid-area: 3 / 1;
  }

  .vd-slider--ticked.vd-slider--ticks-above .vd-slider__ticks {
    grid-area: 1 / 1;
  }

  .vd-slider__label {
    font-family: var(--font-ui);
    font-size: var(--text-body);
    color: var(--ink-soft);
  }

  .vd-slider__value {
    color: var(--accent-text);
    font-weight: 700;
    white-space: nowrap;
  }

  /* Hand-painted: `accent-color` leaves the inactive half near-black. */
  .vd-slider input[type='range'] {
    width: 100%;
    appearance: none;
    background: transparent;
    height: 1.1rem;
    margin: 0;
  }

  @mixin vd-slider-track {
    height: 4px;
    border-radius: 2px;
    background: linear-gradient(
      to right,
      var(--accent) var(--vd-slider-fill, 0%),
      var(--line) var(--vd-slider-fill, 0%)
    );
  }

  @mixin vd-slider-thumb {
    appearance: none;
    width: 1.1rem;
    height: 1.1rem;
    border: none;
    border-radius: 50%;
    background: var(--accent);
  }

  .vd-slider input[type='range']::-webkit-slider-runnable-track {
    @include vd-slider-track;
  }
  .vd-slider input[type='range']::-moz-range-track {
    @include vd-slider-track;
  }
  .vd-slider input[type='range']::-webkit-slider-thumb {
    @include vd-slider-thumb;
    margin-top: -0.35rem;
  }
  .vd-slider input[type='range']::-moz-range-thumb {
    @include vd-slider-thumb;
  }

  /* Split per vendor: one list with both prefixes is dropped whole. */
  .vd-slider--muted input[type='range']::-webkit-slider-runnable-track {
    background: var(--line);
  }
  .vd-slider--muted input[type='range']::-moz-range-track {
    background: var(--line);
  }
  .vd-slider--muted input[type='range']::-webkit-slider-thumb {
    background: var(--ink-mute);
  }
  .vd-slider--muted input[type='range']::-moz-range-thumb {
    background: var(--ink-mute);
  }

  .vd-slider input[type='range']:focus-visible {
    outline: 2px solid var(--accent-text);
    outline-offset: 4px;
    border-radius: 4px;
  }

  .vd-slider__ticks {
    display: flex;
    justify-content: space-between;
    font-family: var(--font-ui);
    font-size: var(--text-label);
    color: var(--ink-mute);
    padding: 0 0.1rem;
    overflow-x: clip;
  }

  .vd-slider__ticks button {
    position: relative;
    appearance: none;
    padding: 0;
    border: none;
    background: none;
    font: inherit;
    color: inherit;
    cursor: pointer;
  }

  .vd-slider__ticks button::after {
    content: '';
    position: absolute;
    inset: -0.6rem -0.75rem;
  }

  /* 44px floor at the smallest glyph. */
  @media (pointer: coarse) {
    .vd-slider__ticks button::after {
      inset: -0.9rem -1.2rem;
    }

    .vd-slider input[type='range'] {
      height: 1.5rem;
    }
  }

  .vd-slider__ticks button:hover,
  .vd-slider__ticks button:active {
    color: var(--ink);
  }

  .vd-slider__ticks button.is-current {
    color: var(--accent-text);
  }

  .vd-slider__ticks button:disabled {
    cursor: not-allowed;
    color: var(--ink-mute);
  }

  .vd-slider__hint {
    margin: 0;
    font-family: var(--font-ui);
    font-size: var(--text-label);
    color: var(--ink-mute);
  }

  /* Never colour alone (WCAG 1.4.1): the hint TEXT states the limit too. */
  .vd-slider__hint.is-out {
    color: var(--color-error-text);
  }

  .vd-slider__field {
    position: relative;
    display: inline-flex;
    align-items: baseline;
    gap: 0.2em;
    color: var(--accent-text);
    font-weight: 700;
    white-space: nowrap;
  }

  .vd-slider__field.is-out {
    color: var(--color-error-text);
  }

  /* At rest it reads as the value it replaced; hover and focus show it takes typing. */
  .vd-slider__field input {
    position: relative;
    z-index: 1;
    width: calc(var(--vd-slider-digits) * 1ch + 0.2em);
    padding: 0 0.1em;
    border: none;
    border-radius: 3px 3px 0 0;
    background: transparent;
    font: inherit;
    color: inherit;
    text-align: center;
    appearance: textfield;
  }

  @supports (field-sizing: content) {
    .vd-slider__field input {
      field-sizing: content;
      width: auto;
      min-width: 1ch;
    }
  }

  .vd-slider__field input:hover,
  .vd-slider__field.is-out input {
    box-shadow: inset 0 -1px currentColor;
  }

  /* The thicker underline and tint are the focus indicator — no box. */
  .vd-slider__field input:focus {
    outline: none;
    box-shadow: inset 0 -2px currentColor;
    background: color-mix(in srgb, var(--accent) 12%, transparent);
  }

  .vd-slider__field input::-webkit-outer-spin-button,
  .vd-slider__field input::-webkit-inner-spin-button {
    appearance: none;
    margin: 0;
  }

  @media (pointer: coarse) {
    .vd-slider__field::after {
      content: '';
      position: absolute;
      inset: calc((100% - 44px) / 2) -0.5rem;
    }
  }
</style>
